import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';

interface Row {
  date: string;
  income: number;
  expenses: number;
}

interface Props {
  data: Row[];
  metric?: 'income' | 'expenses';
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function isoWeekKey(d: Date): string {
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return monday.toISOString().slice(0, 10);
}

function dayIndexMon0(d: Date): number {
  const day = d.getDay();
  return day === 0 ? 6 : day - 1;
}

const DayOfWeekHeatmap: React.FC<Props> = ({ data, metric = 'income' }) => {
  const { theme } = useTheme();

  const { matrix, weeks, max } = useMemo(() => {
    const weekKeys = new Set<string>();
    const cells = new Map<string, number>(); // `${weekKey}-${dayIdx}` → value
    let maxVal = 0;
    data.forEach((r) => {
      const d = new Date(r.date);
      const wk = isoWeekKey(d);
      const di = dayIndexMon0(d);
      weekKeys.add(wk);
      const key = `${wk}-${di}`;
      const val = metric === 'income' ? r.income : r.expenses;
      const next = (cells.get(key) || 0) + (val || 0);
      cells.set(key, next);
      if (next > maxVal) maxVal = next;
    });
    const sortedWeeks = Array.from(weekKeys).sort();
    return { matrix: cells, weeks: sortedWeeks, max: maxVal };
  }, [data, metric]);

  if (weeks.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center', color: theme.gray?.[400] }}>
        No data to plot
      </Box>
    );
  }

  const baseColor = metric === 'income' ? theme.success || '#5B8C7E' : theme.danger || '#C97B63';

  const intensity = (val: number): string => {
    if (max === 0) return theme.gray?.[100] || '#F5F5F5';
    const pct = Math.min(val / max, 1);
    // alpha 0.08 .. 0.95
    const alpha = pct === 0 ? 0 : 0.15 + pct * 0.8;
    return alpha === 0 ? (theme.gray?.[100] || '#F5F5F5') : `${baseColor}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
  };

  const cellSize = 22;

  return (
    <Box sx={{ overflowX: 'auto', pb: 1 }}>
      <Box sx={{ display: 'inline-block', minWidth: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
          {/* Y axis labels */}
          <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1, pt: 2.5 }}>
            {DAY_LABELS.map((d) => (
              <Box key={d} sx={{ height: cellSize + 2, display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '0.7rem', color: theme.gray?.[500] || '#666', width: 32 }}>{d}</Typography>
              </Box>
            ))}
          </Box>
          {/* Columns */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* week header */}
            <Box sx={{ display: 'flex' }}>
              {weeks.map((wk) => (
                <Box key={wk} sx={{ width: cellSize + 2, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.6rem', color: theme.gray?.[500] || '#666' }}>
                    {new Date(wk).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </Typography>
                </Box>
              ))}
            </Box>
            {DAY_LABELS.map((_, di) => (
              <Box key={di} sx={{ display: 'flex' }}>
                {weeks.map((wk) => {
                  const v = matrix.get(`${wk}-${di}`) || 0;
                  return (
                    <Box
                      key={`${wk}-${di}`}
                      title={`${DAY_LABELS[di]} ${new Date(wk).toLocaleDateString()}: ${formatCurrency(v, { showDecimals: false })}`}
                      sx={{
                        width: cellSize,
                        height: cellSize,
                        m: '1px',
                        borderRadius: '4px',
                        backgroundColor: intensity(v),
                        border: `1px solid ${theme.gray?.[200] || '#E5E5E5'}`,
                        transition: 'transform 0.15s ease',
                        '&:hover': { transform: 'scale(1.15)' },
                      }}
                    />
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, ml: 6 }}>
          <Typography sx={{ fontSize: '0.65rem', color: theme.gray?.[500] || '#666' }}>Less</Typography>
          {[0.1, 0.25, 0.5, 0.75, 1].map((p) => (
            <Box
              key={p}
              sx={{
                width: 14,
                height: 14,
                borderRadius: '3px',
                backgroundColor: `${baseColor}${Math.round((0.15 + p * 0.8) * 255).toString(16).padStart(2, '0')}`,
                border: `1px solid ${theme.gray?.[200] || '#E5E5E5'}`,
              }}
            />
          ))}
          <Typography sx={{ fontSize: '0.65rem', color: theme.gray?.[500] || '#666' }}>More</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DayOfWeekHeatmap;
